import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-dreamweaver";
import { Spinner } from "react-bootstrap";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { apiPost } from "../utils/apiAuth";
import config from "../config";
import { Button } from "./ui";

/**
 * Interface for Example data structure
 */
const Example = {
  Example: {
    Inputs: [],
    Output: "",
    Explanation: ""
  }
};

/**
 * Interface for TestCase data structure
 */
const TestCase = {
  Testcase: {
    Value: [],
    Output: ""
  }
};

/**
 * Interface for Question data structure
 */
const Question = {
  Qn_name: "",
  entered_ans: "",
  score: "",
  status: false,
  Qn: "",
  Ans: "",
  Name: "",
  QNty: "",
  QnTe: "",
  QnTy: "",
  Tags: [],
  test: [],
  Hints: [],
  Level: "",
  Table: "",
  Examples: [],
  Template: "",
  ConceptID: "",
  CreatedBy: "",
  CreatedON: "",
  TestCases: [],
  LastUpdated: "",
  MultiSelect: "",
  Explanations: [],
  FunctionCall: "",
  QuestionType: ""
};

/**
 * FastAPI Backend Response Interfaces
 */
const FastAPIHealthResponse = {
  status: "",
  timestamp: "",
  version: "",
  redis_connected: false,
  docker_available: false,
  queue_length: 0
};

const FastAPISubmitResponse = {
  submission_id: "",
  status: "",
  message: "",
  estimated_wait_time: 0
};

const FastAPIStatusResponse = {
  submission_id: "",
  status: "",
  result: {
    success: false,
    parsed_results: [],
    raw_output: "",
    actual_output: "",
    exit_code: 0,
    execution_time: 0,
    error: "",
    results: [],
    compilation_error: false,
    error_type: "",
    line_number: 0,
    offset: 0
  },
  error: null,
  execution_time: 0,
  created_at: "",
  completed_at: "",
  queue_position: 0
};

/**
 * Coding Questions Component
 * Integrated with existing sidebar layout
 */
const CodingQuestions = () => {
  // ===== STATE MANAGEMENT =====
  const navigate = useNavigate();
  const returnPath = useMemo(() => {
    try {
      return sessionStorage.getItem('codingQuestions_returnPath') || '/content-bank';
    } catch {
      return '/content-bank';
    }
  }, []);
  const returnLabel = returnPath === '/project-content' ? 'Back to Project Content' : 'Back to Content Bank';

  const handleGoBack = () => {
    navigate(returnPath);
  };
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pythonCode, setPythonCode] = useState("");
  const [output, setOutput] = useState("");
  const [processing, setProcessing] = useState(false);
  const [runResponseTestCases, setRunResponseTestCases] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [backendHealthy, setBackendHealthy] = useState(false);
  const [executionStatus, setExecutionStatus] = useState('idle');
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(null);
  const [activeSection, setActiveSection] = useState('output');

  // ===== URL / Session PARAMETERS =====
  const location = useLocation();
  const fallbackParams = (() => {
    try {
      if (location && location.state) return location.state;
      // Build fallback from individual sessionStorage keys
      const obj = {};
      const subjectId = sessionStorage.getItem('codingQuestions_subjectId');
      const topicId = sessionStorage.getItem('codingQuestions_topicId');
      const subTopicId = sessionStorage.getItem('codingQuestions_subTopicId');
      const level = sessionStorage.getItem('codingQuestions_level');
      const questionId = sessionStorage.getItem('codingQuestions_questionId');
      const editor = sessionStorage.getItem('codingQuestions_editor');
      
      if (subjectId) obj.subject_id = JSON.parse(subjectId);
      if (topicId) obj.topic_id = JSON.parse(topicId);
      if (subTopicId) obj.sub_topic_id = JSON.parse(subTopicId);
      if (level) obj.level = JSON.parse(level);
      if (questionId) obj.question_id = JSON.parse(questionId);
      if (editor) obj.editor = JSON.parse(editor);
      
      return obj;
    } catch (e) {
      return {};
    }
  })();

  const subjectId = fallbackParams.subject_id || null;
  const topicId = fallbackParams.topic_id || null;
  const subTopicId = fallbackParams.sub_topic_id || null;
  const level = fallbackParams.level || null;
  const questionId = fallbackParams.question_id || null;
  const editor = fallbackParams.editor || null;
  
  // Debug URL parameters

  // ===== API CONFIGURATION =====
  const API_BASE_URL = `${process.env.REACT_APP_PYEXE_BASE_URL}api/student/python-coding/`;

  // ===== DATA MAPPING FUNCTIONS =====
  
  /**
   * Maps question_data JSON to component expected format
   */
  const mapQuestionData = (questionData, questionId) => {
    try {
      const parsed = typeof questionData === 'string' ? JSON.parse(questionData) : questionData;
      
      const mapped = {
        Qn_name: questionId,
        entered_ans: "",
        score: "",
        status: false,
        Qn: parsed.Qn || "",
        Ans: parsed.Ans || "",
        Name: parsed.Page_Name || "",
        QNty: "",
        QnTe: "",
        QnTy: "coding",
        Tags: parsed.Tags || [],
        test: [],
        Hints: parsed.Hints || [],
        Level: parsed.level || "",
        Table: "",
        Examples: parsed.Examples || [],
        Template: parsed.Ans || "",
        ConceptID: "",
        CreatedBy: parsed.CreatedBy || "",
        CreatedON: parsed.CreatedOn || "",
        TestCases: parsed.TestCases || [],
        LastUpdated: parsed.LastUpdated || "",
        MultiSelect: "",
        Explanations: parsed.Explanations || [],
        FunctionCall: parsed.FunctionCall || "",
        QuestionType: "coding"
      };
      return mapped;
    } catch (error) {
      return {
        Qn_name: questionId,
        Qn: "Error parsing question data",
        Ans: "",
        Examples: [],
        TestCases: [],
        FunctionCall: "",
        QuestionType: "coding"
      };
    }
  };

  // ===== FASTAPI BACKEND INTEGRATION =====
  
  /**
   * Checks the health status of the FastAPI backend
   */
  const checkBackendHealth = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PYEXE_BASE_URL}health`);
      const data = await response.json();
      setBackendHealthy(data.status === 'healthy');
    } catch (error) {
      setBackendHealthy(false);
    }
  };

  /**
   * Starts periodic health checks every 10 seconds
   */
  useEffect(() => {
    checkBackendHealth();
    const interval = setInterval(checkBackendHealth, 10000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Submits code to FastAPI backend for execution
   */
  const submitCodeToBackend = async (code, testCases, timeout = 15, questionId = "q123", testId = "practice") => {
    // Transform test cases to match the expected API format
    const transformedTestCases = testCases.map(testCase => {
      if (Array.isArray(testCase.Testcase)) {
        return {
          Testcase: testCase.Testcase
        };
      } else if (testCase.Testcase && testCase.Testcase.Value) {
        return {
          Testcase: {
            Value: testCase.Testcase.Value,
            Output: testCase.Testcase.Output
          }
        };
      } else {
        return {
          Testcase: {
            Value: Array.isArray(testCase) ? testCase : [testCase],
            Output: "validation_check"
          }
        };
      }
    });

    const currentQuestion = questions[currentQuestionIndex];
    const functionCall = currentQuestion?.FunctionCall || "";

    const payload = {
      code: code,
      TestCases: transformedTestCases,
      FunctionCall: functionCall,
      language: "python",
      timeout: timeout,
      memory_limit: timeout === 10 ? "100m" : "200m",
      user_id: "25SABCXIS019",
      question_id: questionId,
      test_id: testId
    };

    const response = await fetch(`${process.env.REACT_APP_PYEXE_BASE_URL}api/v1/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.submission_id;
  };

  /**
   * Polls the execution status of a submission
   */
  const pollExecutionStatus = async (submissionId, maxWaitTime = 30) => {
    const startTime = Date.now();
    const pollInterval = 1000;

    while (Date.now() - startTime < maxWaitTime * 1000) {
      try {
        const response = await fetch(`${process.env.REACT_APP_PYEXE_BASE_URL}api/v1/execute/${submissionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        const data = await response.json();
        
        if (data.status === 'completed') {
          return data;
        }
        
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        throw error;
      }
    }
    
    throw new Error(`Execution timeout after ${maxWaitTime} seconds`);
  };

  /**
   * Processes test cases to handle mixed format
   */
  const processTestCases = (testCases) => {
    return testCases.map(testCase => {
      if (Array.isArray(testCase.Testcase)) {
        return testCase;
      } else if (testCase.Testcase && testCase.Testcase.Value) {
        return testCase;
      } else {
        return {
          Testcase: testCase.Testcase.Value || []
        };
      }
    });
  };

  /**
   * Extract mandatory keywords from the first test case
   */
  const extractMandatoryKeywords = (testCases) => {
    if (!testCases || testCases.length === 0) return [];
    
    const firstTestCase = testCases[0];
    if (!firstTestCase || !firstTestCase.Testcase) return [];
    
    const testCaseValue = Array.isArray(firstTestCase.Testcase) 
      ? firstTestCase.Testcase 
      : firstTestCase.Testcase.Value;
    
    if (!Array.isArray(testCaseValue)) return [];
    
    return testCaseValue.filter(keyword => !keyword.includes('def'));
  };

  /**
   * Generate editor value with FunctionCall appended to template
   */
  const generateEditorValue = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const template = currentQuestion?.Template || "";
    const functionCall = currentQuestion?.FunctionCall || "";
    const Ans = currentQuestion?.Ans || "";
    
    if (pythonCode && pythonCode.trim() !== "") {
      return pythonCode;
    }
    
    if (hasUserInteracted && pythonCode === "") {
      return "";
    }
    
    if (Ans) {
      if (functionCall) {
        setPythonCode(Ans + '\n\n\n\n\n' + functionCall);
        return Ans + '\n\n\n\n\n' + functionCall;
      }
      setPythonCode(Ans);
      return Ans;
    }
    
    return "";
  };

  // ===== DATA FETCHING =====
  
  /**
   * Fetches coding questions from the hierarchy API
   */
  const fetchCodingQuestions = useCallback(async () => {
    try {
      setLoading(true);
      
      const payload = {
        subject_id: subjectId,
        topic_id: topicId,
        sub_topic_id: subTopicId,
        level: level || null, // Handle empty level
        question_type: "coding",
        page: 1,
        limit: 100 // Get all questions
      };
      const response = await apiPost(`${config.API_BASE_URL}/api/get-questions-hierarchical/`, payload);
      
      if (response.status === 200 && response.data && response.data.questions) {
        
        // Map the questions to the expected format
        const mappedQuestions = response.data.questions.map(q => {
          const mappedQuestion = mapQuestionData(q.question_data, q.question_id);
          const processedTestCases = processTestCases(mappedQuestion.TestCases);
          return { ...mappedQuestion, TestCases: processedTestCases };
        });
        setQuestions(mappedQuestions);
        
        // Find the starting question index
        if (questionId) {
          const startIndex = mappedQuestions.findIndex(q => q.Qn_name === questionId);
          if (startIndex !== -1) {
            setCurrentQuestionIndex(startIndex);
          }
        }
        
        // Set initial question with empty editor
        if (mappedQuestions.length > 0) {
          setPythonCode('');
        }
      } else {
        setQuestions([]);
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [subjectId, topicId, subTopicId, level, questionId]);

  useEffect(() => {
    fetchCodingQuestions();
  }, [fetchCodingQuestions]);

  // ===== CODE EDITOR HANDLERS =====
  
  /**
   * Handles code changes in the AceEditor
   */
  const handleCodeChange = (newCode) => {
    setPythonCode(newCode);
    setHasUserInteracted(true);
  };

  /**
   * Handles switching between different questions
   */
  const handleQuestionChange = (index) => {
    setCurrentQuestionIndex(index);
    setPythonCode('');
    setOutput('');
    setRunResponseTestCases([]);
    setSuccessMessage('');
    setAdditionalMessage('');
    setHasUserInteracted(false);
    setSelectedTestCaseIndex(null);
    setActiveSection('output');
  };

  // ===== FASTAPI CODE EXECUTION =====
  
  /**
   * Code execution with test cases (RUN CODE button)
   */
  const handleRunCode = async () => {
    if (!pythonCode.trim()) {
      return;
    }

    setProcessing(true);
    setExecutionStatus('submitting');
    setOutput('');
    setSuccessMessage('');
    setAdditionalMessage('');
    setRunResponseTestCases([]);
    setSelectedTestCaseIndex(null);
    setActiveSection('output');

    try {
      const currentQuestion = questions[currentQuestionIndex];
      const testCases = currentQuestion?.TestCases || [];

      const submissionId = await submitCodeToBackend(pythonCode, testCases, 10, currentQuestion.Qn_name, "practice");
      setExecutionStatus('executing');

      const result = await pollExecutionStatus(submissionId, 15);
      
      if (result.result.success) {
        setOutput(result.result.actual_output);
        
        if (Array.isArray(result.result.parsed_results)) {
          const testCaseResults = result.result.parsed_results.map((testCase, index) => {
            return {
              id: `TestCase${index + 1}`,
              passed: testCase.passed,
              input: testCase.input || testCase.test_case || '',
              expected: testCase.expected || testCase.expected_output || '',
              actual: testCase.result,
              execution_time: testCase.execution_time || 0
            };
          });
          
          const allPassed = result.result.parsed_results.every((testCase) => testCase.passed);
          const finalResult = { 
            id: "Result",
            passed: allPassed,
            message: allPassed ? "Passed" : "Failed"
          };
          
          const finalTestCases = [...testCaseResults, finalResult];
          setRunResponseTestCases(finalTestCases);
          
          if (allPassed) {
            setSuccessMessage("Congratulations!");
            setAdditionalMessage("You have passed all the test cases.");
          } else {
            setSuccessMessage("Wrong Answer");
            setAdditionalMessage("You have not passed all the test cases.");
          }
        }
      } else {
        const errorMessage = result.result.error;
        setOutput(`Error: ${errorMessage}`);
        setSuccessMessage('❌ Execution Failed');
      }
      
      setExecutionStatus('completed');
    } catch (error) {
      setOutput(`Error: ${error instanceof Error ? error.message : 'Execution failed'}`);
      setSuccessMessage('❌ Execution Failed');
      setExecutionStatus('error');
    } finally {
      setProcessing(false);
    }
  };

  // ===== RENDERING =====

  const pageShellSx = {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#f2eeee',
  };

  const pageHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        flexShrink: 0,
        borderBottom: '1px solid var(--color-border-default)',
        backgroundColor: 'var(--color-bg-paper)',
      }}
    >
      <Button
        variant="ghost"
        size="small"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={handleGoBack}
      >
        {returnLabel}
      </Button>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
        Coding Questions
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={pageShellSx}>
        {pageHeader}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading coding questions...</p>
          </div>
        </Box>
      </Box>
    );
  }

  if (!loading && questions.length === 0) {
    return (
      <Box sx={pageShellSx}>
        {pageHeader}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center">
            <h5>No coding questions found</h5>
            <p className="text-muted">Please check your filters or try a different subject/topic.</p>
            <Button variant="primary" onClick={handleGoBack}>
              {returnLabel}
            </Button>
          </div>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageShellSx}>
      {pageHeader}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          backgroundColor: '#f2eeee',
        }}
      >
        {/* Question navigation */}
        <Box
          sx={{
            width: 88,
            flexShrink: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 1.5,
            px: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            boxSizing: 'border-box',
          }}
        >
          {questions.map((_, index) => (
            <Box
              component="button"
              key={index}
              type="button"
              onClick={() => handleQuestionChange(index)}
              sx={{
                width: 52,
                height: 52,
                minWidth: 52,
                maxWidth: 52,
                minHeight: 52,
                m: 0,
                p: 0,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: currentQuestionIndex === index ? '#2ecc71' : 'var(--color-border-default, #e2e8f0)',
                borderRadius: '8px',
                backgroundColor: currentQuestionIndex === index ? '#42FF58' : '#FFFFFF',
                color: '#000',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                fontSize: '0.8rem',
                fontWeight: 600,
                lineHeight: 1,
                fontFamily: 'inherit',
                appearance: 'none',
                WebkitAppearance: 'none',
                boxSizing: 'border-box',
                '&:hover': {
                  backgroundColor: currentQuestionIndex === index ? '#42FF58' : '#f8fafc',
                },
              }}
            >
              Q{index + 1}
            </Box>
          ))}
        </Box>

        {/* Question statement */}
        <Box
          sx={{
            flex: '0 0 40%',
            minWidth: 280,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            mr: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, bgcolor: 'rgba(229, 229, 229, 0.2)' }}>
                        <h6 className="mb-3" style={{ color: "#333", fontWeight: "bold" }}>
                          Question {currentQuestionIndex + 1} of {questions.length}
                          <span style={{ color: "#666", fontSize: "14px", fontWeight: "normal", marginLeft: "10px" }}>
                            (ID: {questions[currentQuestionIndex]?.Qn_name})
                          </span>
                        </h6>
                        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{questions[currentQuestionIndex]?.Qn}</pre>
                        
                        {/* ===== MANDATORY KEYWORDS SECTION ===== */}
                        {(() => {
                          const currentQuestion = questions[currentQuestionIndex];
                          const mandatoryKeywords = extractMandatoryKeywords(currentQuestion?.TestCases || []);
                          
                          if (mandatoryKeywords.length > 0) {
                            return (
                              <div className="mt-4">
                                <h6 style={{ color: "#333", fontWeight: "bold", marginBottom: "10px" }}>Use keywords:</h6>
                                <div className="mb-3 p-3" style={{ 
                                  backgroundColor: "#f8f9fa", 
                                  border: "1px solid #dee2e6", 
                                  borderRadius: "8px",
                                  fontSize: "13px"
                                }}>
                                  <span style={{ color: "#212529" }}>
                                    {mandatoryKeywords.join(', ')}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* ===== EXAMPLES SECTION ===== */}
                        {questions[currentQuestionIndex]?.Examples && questions[currentQuestionIndex].Examples.length > 0 && (
                          <div className="mt-4">
                            <h6 style={{ color: "#333", fontWeight: "bold", marginBottom: "10px" }}>Examples:</h6>
                            {questions[currentQuestionIndex].Examples.map((example, index) => (
                              <div key={index} className="mb-3 p-3" style={{ 
                                backgroundColor: "#f8f9fa", 
                                border: "1px solid #dee2e6", 
                                borderRadius: "8px",
                                fontSize: "13px"
                              }}>
                                <div className="mb-2">
                                  <strong style={{ color: "#495057" }}>Example {index + 1}:</strong>
                                </div>
                                
                                {example.Example.Inputs && example.Example.Inputs.length > 0 && (
                                  <div className="mb-2">
                                    <span style={{ color: "#6c757d", fontWeight: "500" }}>Input: </span>
                                    <span style={{ color: "#212529" }}>
                                      {example.Example.Inputs.join(", ")}
                                    </span>
                                  </div>
                                )}
                                
                                {example.Example.Output && (
                                  <div className="mb-2">
                                    <span style={{ color: "#6c757d", fontWeight: "500" }}>Output: </span>
                                    <span style={{ color: "#212529" }}>
                                      {example.Example.Output}
                                    </span>
                                  </div>
                                )}
                                
                                {example.Example.Explanation && (
                                  <div>
                                    <span style={{ color: "#6c757d", fontWeight: "500" }}>Explanation: </span>
                                    <span style={{ color: "#212529" }}>
                                      {example.Example.Explanation}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
          </Box>
        </Box>

        {/* Editor + results */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              bgcolor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, p: 1.5 }}>
              <AceEditor
                mode="python"
                theme="dreamweaver"
                onChange={handleCodeChange}
                value={generateEditorValue()}
                fontSize={14}
                showPrintMargin={false}
                wrapEnabled={true}
                style={{ width: '100%', height: '100%' }}
                placeholder={questions[currentQuestionIndex]?.Template || questions[currentQuestionIndex]?.FunctionCall ? "" : `Write your Code here.

Instructions :
1. Don't use input() function. 
2. It is mandatory to use the exact variable names provided in the question or example [variable names are case-sensitive ]

`}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              height: 44,
              px: 1.5,
              bgcolor: 'rgba(229, 229, 229, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            className="processingDiv"
          >
                        <div className="d-flex flex-column justify-content-center">
                          {processing ? (
                            <h5 className="m-0 processingDivHeadingTag">Processing...</h5>
                          ) : (
                            <>
                              {successMessage && <h5 className="m-0 ps-1" style={{ fontSize: '14px' }}>{successMessage}</h5>}
                              {additionalMessage && <p className="processingDivParaTag m-0 ps-1" style={{ fontSize: "10px" }}>{additionalMessage}</p>}
                            </>
                          )}
                        </div>
                        <div className="d-flex justify-content-end align-items-center">
                          {/* Run Code Button */}
                          <button
                            className="btn btn-sm btn-light me-2 processingDivButton"
                            style={{
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                              minWidth: "70px",
                              boxShadow: "#888 1px 2px 5px 0px",
                              height: "30px",
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px"
                            }}
                            onClick={handleRunCode}
                            disabled={processing || !pythonCode.trim() || !backendHealthy}
                          >
                            {/* Health Status Indicator */}
                            <div 
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: backendHealthy ? "#10B981" : "#EF4444",
                                border: `1px solid ${backendHealthy ? "#059669" : "#DC2626"}`,
                                boxShadow: backendHealthy 
                                  ? "0 0 4px rgba(16, 185, 129, 0.6)" 
                                  : "0 0 4px rgba(239, 68, 68, 0.6)",
                                animation: backendHealthy ? "pulse-green-small 2s ease-in-out infinite" : "pulse-red-small 2s ease-in-out infinite",
                                flexShrink: 0
                              }}
                              title={backendHealthy ? "Backend Connected" : "Backend Disconnected"}
                            />
                            RUN CODE
                          </button>
                        </div>
          </Box>

          <Box
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              bgcolor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {/* ===== SECTION TABS ===== */}
                        <div className="d-flex mb-3" style={{ flexShrink: 0 }}>
                          <button
                            className={`btn ${activeSection === 'output' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
                            onClick={() => setActiveSection('output')}
                            style={{ fontSize: "12px", padding: "6px 12px" }}
                          >
                            Output
                          </button>
                          <button
                            className={`btn ${activeSection === 'testcases' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActiveSection('testcases')}
                            style={{ fontSize: "12px", padding: "6px 12px" }}
                          >
                            Test Cases
                          </button>
                        </div>

                        {/* ===== CODE EXECUTION OUTPUT ===== */}
                        {activeSection === 'output' && (
                          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            {output && (
                              <>
                                <pre
                                  className="m-0"
                                  style={{
                                    fontSize: "12px",
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    backgroundColor: "#f8f9fa",
                                    padding: "10px",
                                    borderRadius: "4px",
                                    border: "1px solid #e9ecef",
                                    margin: "0",
                                    overflow: "auto"
                                  }}
                                >
                                  {output}
                                </pre>
                              </>
                            )}
                          </Box>
                        )}
                        
                        {/* ===== TEST CASE RESULTS ===== */}
                        {activeSection === 'testcases' && runResponseTestCases && runResponseTestCases.length > 0 && (
                          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {/* Two-column layout for test cases */}
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                              {/* Left Column - Test Case List (20%) */}
                              <div className="border-end" style={{ 
                                width: "20%", 
                                overflowY: "auto", 
                                padding: "1px 10px 1px 10px",
                                scrollbarWidth: "thin",
                                scrollbarColor: "#c1c1c1 #f1f1f1"
                              }}>
                                {runResponseTestCases.map((testCase, index) => (
                                  <div
                                    key={index}
                                    className={`p-2 border-bottom cursor-pointer ${
                                      selectedTestCaseIndex === index ? 'text-primary' : ''
                                    }`}
                                    style={{ 
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      minHeight: "40px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      margin: "6px 0px",
                                      borderRadius: "8px",
                                      padding: "8px 12px",
                                      backgroundColor: selectedTestCaseIndex === index ? '#f2f2f0' : '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      color: selectedTestCaseIndex === index ? '#007bff' : '#212529'
                                    }}
                                    onClick={() => setSelectedTestCaseIndex(index)}
                                  >
                                    <span>{testCase.id}</span>
                                    {testCase.passed ? (
                                      <span className="text-success">✓</span>
                                    ) : (
                                      <span className="text-danger">✗</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Right Column - Test Case Details (80%) */}
                              <div className="px-5 pt-1 pb-3" style={{ width: "80%", overflowY: "auto" }}>
                                {selectedTestCaseIndex !== null && runResponseTestCases[selectedTestCaseIndex] && (
                                  <div>
                                    {/* Test Case Status */}
                                    <div className="mb-3">
                                      <strong>Status: </strong>
                                      <span className={runResponseTestCases[selectedTestCaseIndex].passed ? "text-success" : "text-danger"}>
                                        {runResponseTestCases[selectedTestCaseIndex].passed ? "Passed" : "Failed"}
                                      </span>
                                    </div>
                                    
                                    {/* For first test case (keyword validation) - show only status and actual output */}
                                    {selectedTestCaseIndex === 0 ? (
                                      <>
                                        <div className="mb-3">
                                          <strong>Type: </strong>
                                          <span className="text-info">Keyword Validation</span>
                                        </div>
                                        
                                        {/* Actual Output for keyword validation */}
                                        <div className="mb-3">
                                          <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: "11px", fontFamily: "monospace" }}>
                                            {(() => {
                                              const currentQuestion = questions[currentQuestionIndex];
                                              if (currentQuestion?.TestCases?.[selectedTestCaseIndex]) {
                                                const testCaseData = currentQuestion.TestCases[selectedTestCaseIndex];
                                                let value;
                                                
                                                if (Array.isArray(testCaseData.Testcase)) {
                                                  value = testCaseData.Testcase;
                                                } else if (testCaseData.Testcase && testCaseData.Testcase.Value) {
                                                  value = testCaseData.Testcase.Value;
                                                }
                                                
                                                if (value && Array.isArray(value)) {
                                                  return value.map((item, index) => {
                                                    const itemString = String(item);
                                                    if (itemString.includes('\\n')) {
                                                      return itemString.split('\\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    } else if (itemString.includes('\n')) {
                                                      return itemString.split('\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    }
                                                    return (
                                                      <React.Fragment key={index}>
                                                        {itemString}
                                                        {index < value.length - 1 && <br />}
                                                      </React.Fragment>
                                                    );
                                                  });
                                                }
                                              }
                                              return "No output";
                                            })()}
                                          </div>
                                        </div>
                                      </>
                                    ) : selectedTestCaseIndex > 0 && runResponseTestCases[selectedTestCaseIndex]?.id !== "Result" ? (
                                      <>
                                        {/* Input */}
                                        <div className="mb-3">
                                          <strong>Input:</strong>
                                          <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: "11px", fontFamily: "monospace" }}>
                                            {(() => {
                                              const currentQuestion = questions[currentQuestionIndex];
                                              const testCaseData = currentQuestion?.TestCases?.[selectedTestCaseIndex];
                                              if (testCaseData?.Testcase && typeof testCaseData.Testcase === 'object' && 'Value' in testCaseData.Testcase && testCaseData.Testcase.Value !== undefined && testCaseData.Testcase.Value !== null) {
                                                const value = testCaseData.Testcase.Value;
                                                if (Array.isArray(value)) {
                                                  return value.map((item, index) => {
                                                    const itemString = String(item);
                                                    if (itemString.includes('\\n')) {
                                                      return itemString.split('\\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    } else if (itemString.includes('\n')) {
                                                      return itemString.split('\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    }
                                                    return (
                                                      <React.Fragment key={index}>
                                                        {itemString}
                                                        {index < value.length - 1 && <br />}
                                                      </React.Fragment>
                                                    );
                                                  });
                                                } else {
                                                  const stringValue = String(value);
                                                  if (stringValue.includes('\\n')) {
                                                    return stringValue.split('\\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  } else if (stringValue.includes('\n')) {
                                                    return stringValue.split('\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  }
                                                  return stringValue;
                                                }
                                              }
                                              return "No input data";
                                            })()}
                                          </div>
                                        </div>
                                        
                                        {/* Expected Output */}
                                        <div className="mb-3">
                                          <strong>Expected Output:</strong>
                                          <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: "11px", fontFamily: "monospace" }}>
                                            {(() => {
                                              const currentQuestion = questions[currentQuestionIndex];
                                              const testCaseData = currentQuestion?.TestCases?.[selectedTestCaseIndex];
                                              if (testCaseData?.Testcase && typeof testCaseData.Testcase === 'object' && 'Output' in testCaseData.Testcase && testCaseData.Testcase.Output !== undefined && testCaseData.Testcase.Output !== null) {
                                                const value = testCaseData.Testcase.Output;
                                                if (Array.isArray(value)) {
                                                  return value.map((item, index) => {
                                                    const itemString = String(item);
                                                    if (itemString.includes('\\n')) {
                                                      return itemString.split('\\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    } else if (itemString.includes('\n')) {
                                                      return itemString.split('\n').map((line, lineIndex) => (
                                                        <React.Fragment key={`${index}-${lineIndex}`}>
                                                          {line}
                                                          {lineIndex < itemString.split('\n').length - 1 && <br />}
                                                        </React.Fragment>
                                                      ));
                                                    }
                                                    return (
                                                      <React.Fragment key={index}>
                                                        {itemString}
                                                        {index < value.length - 1 && <br />}
                                                      </React.Fragment>
                                                    );
                                                  });
                                                } else {
                                                  const stringValue = String(value);
                                                  if (stringValue.includes('\\n')) {
                                                    return stringValue.split('\\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  } else if (stringValue.includes('\n')) {
                                                    return stringValue.split('\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  }
                                                  return stringValue;
                                                }
                                              }
                                              return "No expected output";
                                            })()}
                                          </div>
                                        </div>
                                        
                                        {/* Actual Output */}
                                        <div className="mb-3">
                                          <strong>Actual Output:</strong>
                                          <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: "11px", fontFamily: "monospace" }}>
                                            {(() => {
                                              if (runResponseTestCases[selectedTestCaseIndex]?.actual !== undefined && runResponseTestCases[selectedTestCaseIndex]?.actual !== null) {
                                                const value = runResponseTestCases[selectedTestCaseIndex].actual;
                                                if (Array.isArray(value)) {
                                                  return `[${value.join(', ')}]`;
                                                } else {
                                                  const stringValue = String(value);
                                                  if (stringValue.includes('\\n')) {
                                                    return stringValue.split('\\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  } else if (stringValue.includes('\n')) {
                                                    return stringValue.split('\n').map((line, index) => (
                                                      <React.Fragment key={index}>
                                                        {line}
                                                        {index < stringValue.split('\n').length - 1 && <br />}
                                                      </React.Fragment>
                                                    ));
                                                  }
                                                  return stringValue;
                                                }
                                              }
                                              return "No output";
                                            })()}
                                          </div>
                                        </div>
                                      </>
                                    ) : null}
                                    
                                    {/* Execution Time - only show for non-Result and non-first test cases */}
                                    {runResponseTestCases[selectedTestCaseIndex]?.id !== "Result" && selectedTestCaseIndex !== 0 && (
                                      <div className="mt-3">
                                        <strong>Execution Time: </strong>
                                        <span className="text-muted">
                                          {(() => {
                                            const testCase = runResponseTestCases[selectedTestCaseIndex];
                                            if (testCase?.execution_time) {
                                              return `${(testCase.execution_time * 1000).toFixed(3)}ms`;
                                            }
                                            return "N/A";
                                          })()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Default message when no test case is selected */}
                                {selectedTestCaseIndex === null && (
                                  <div className="text-center text-muted" style={{ marginTop: "50px" }}>
                                    Click on a test case to view details
                                  </div>
                                )}
                              </div>
                            </Box>
                          </Box>
                        )}
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* ===== CSS ANIMATIONS FOR HEALTH INDICATOR ===== */}
      <style>
        {`
          @keyframes pulse-green-small {
            0% {
              transform: scale(1);
              opacity: 1;
              box-shadow: 0 0 4px rgba(16, 185, 129, 0.6);
            }
            50% {
              transform: scale(1.2);
              opacity: 0.8;
              box-shadow: 0 0 6px rgba(16, 185, 129, 0.8);
            }
            100% {
              transform: scale(1);
              opacity: 1;
              box-shadow: 0 0 4px rgba(16, 185, 129, 0.6);
            }
          }
          
          @keyframes pulse-red-small {
            0% {
              transform: scale(1);
              opacity: 1;
              box-shadow: 0 0 4px rgba(239, 68, 68, 0.6);
            }
            50% {
              transform: scale(1.2);
              opacity: 0.8;
              box-shadow: 0 0 6px rgba(239, 68, 68, 0.8);
            }
            100% {
              transform: scale(1);
              opacity: 1;
              box-shadow: 0 0 4px rgba(239, 68, 68, 0.6);
            }
          }
        `}
      </style>
      
      {/* ===== PLACEHOLDER STYLING ===== */}
      <style>
        {`
          .ace_comment.ace_placeholder {
            margin: 0 !important;
            padding: 0 !important;
          }
        `}
      </style>
    </Box>
  );
};

export default CodingQuestions;

